import React from 'react';
import { Box, Drawer, List, ListItem, ListItemButton, ListItemText, Typography, IconButton, CircularProgress, Divider } from '@mui/material';
import { X, Plus } from 'lucide-react';
import { TAIConversation } from '../../../../store/useTAIChatStore';

interface ConversationHistoryProps {
  open: boolean;
  onClose: () => void;
  conversations: TAIConversation[];
  loading: boolean;
  currentConversationId: number | null;
  onSelectConversation: (conversationId: number) => void;
  onNewChat: () => void;
}

function formatConversationTitle(conversation: TAIConversation): string {
  if (conversation.title) {
    return conversation.title;
  }

  const date = new Date(conversation.createdAt);
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInHours = diffInMs / (1000 * 60 * 60);
  const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

  if (diffInHours < 24) {
    return 'Today';
  } else if (diffInDays < 2) {
    return 'Yesterday';
  } else if (diffInDays < 7) {
    return `${Math.floor(diffInDays)} days ago`;
  } else {
    return date.toLocaleDateString();
  }
}

export function ConversationHistory({
  open,
  onClose,
  conversations,
  loading,
  currentConversationId,
  onSelectConversation,
  onNewChat,
}: ConversationHistoryProps) {
  // Sort conversations by lastMessageAt or createdAt (most recent first)
  const sortedConversations = [...conversations].sort((a, b) => {
    const dateA = new Date(a.lastMessageAt || a.createdAt).getTime();
    const dateB = new Date(b.lastMessageAt || b.createdAt).getTime();
    return dateB - dateA; // Descending order (newest first)
  });

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 280,
          bgcolor: '#FFF',
          borderRight: '1px solid rgba(0,0,0,0.08)',
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderBottom: '1px solid rgba(0,0,0,0.08)',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '18px' }}>
            Chat History
          </Typography>
          <IconButton
            size="small"
            onClick={onClose}
            sx={{ color: 'black', borderRadius: 2, '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' } }}
          >
            <X size={20} />
          </IconButton>
        </Box>

        {/* New Chat Button */}
        {currentConversationId && (
          <Box sx={{ p: 2 }}>
            <ListItemButton
              onClick={() => {
                onNewChat();
                onClose();
              }}
              sx={{
                borderRadius: 2,
                border: '1px solid rgba(0,0,0,0.12)',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.04)',
                },
              }}
            >
              <Plus size={20} style={{ marginRight: 8 }} />
              <ListItemText
                primary="New Chat"
                primaryTypographyProps={{
                  fontWeight: 600,
                  fontSize: '14px',
                }}
              />
            </ListItemButton>
          </Box>
        )}

        {/* Conversations List */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress size={32} sx={{ color: '#222' }} />
            </Box>
          ) : conversations.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No conversations yet
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 1 }}>
              {sortedConversations.map((conversation) => (
                <ListItem key={conversation.id} disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => {
                      onSelectConversation(conversation.id);
                      onClose();
                    }}
                    selected={currentConversationId === conversation.id}
                    sx={{
                      borderRadius: 2,
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(0,0,0,0.08)',
                        '&:hover': {
                          backgroundColor: 'rgba(0,0,0,0.12)',
                        },
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(0,0,0,0.04)',
                      },
                    }}
                  >
                    <ListItemText
                      primary={formatConversationTitle(conversation)}
                      secondary={
                        conversation.lastMessageAt
                          ? new Date(conversation.lastMessageAt).toLocaleString()
                          : null
                      }
                      primaryTypographyProps={{
                        fontWeight: currentConversationId === conversation.id ? 600 : 400,
                        fontSize: '14px',
                        noWrap: true,
                      }}
                      secondaryTypographyProps={{
                        fontSize: '12px',
                        noWrap: true,
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Box>
    </Drawer>
  );
}

